/**
 * WorkflowsPage - admin surface for the generic `apps.workflows` engine.
 *
 * The canonical authoring location for every workflow resource type
 * (holding, claim, …). Replaces the read-only "Workflow Rules &
 * Predicates" panel that used to live under Settings → Registration.
 *
 * Shell: `<MasterDetail>` with the left panel grouped by
 * `resource_type`, the right panel hosting the three-tab detail
 * (General / Config / Settings). Selection is synced to the
 * `?wf=<uuid>` query param so the URL can be deep-linked into a
 * specific workflow.
 *
 * Gated server-side by the `workflow_designer` feature flag; the
 * route in `App.tsx` wraps this page in `<ProtectedRoute flag=...>`
 * so unauthorised tenants never mount the hook tree.
 */

import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { useWorkflowDefinitions } from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { WorkflowList } from './workflow-list'
import { WorkflowDetail } from './workflow-detail'
import { WorkflowCreateDialog } from './workflow-create-dialog'
import { useState } from 'react'

export function WorkflowsPage() {
  const { data: workflows, isLoading } = useWorkflowDefinitions()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const selectedUuid = searchParams.get('wf')

  function selectWorkflow(uuid: string | null) {
    if (uuid) {
      setSearchParams({ wf: uuid }, { replace: false })
    } else {
      searchParams.delete('wf')
      setSearchParams(searchParams, { replace: true })
    }
  }

  return (
    <AppShell>
      <main className="page-container py-6">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h1 className="font-display text-[28px] text-[var(--ink)]">
              Workflow Designer
            </h1>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              Create and edit the state machines that drive registrations,
              claims, and every other workflow-backed resource. Edits apply to
              the current version; in-flight instances see the new definition
              on their next transition.
            </p>
          </div>
        </div>

        <MasterDetail
          testId="workflows"
          title="Workflows"
          onAdd={() => setShowCreateDialog(true)}
          hasSelection={!!selectedUuid}
          selectedId={selectedUuid}
          visibleItemIds={(workflows ?? []).map((wf) => wf.uuid)}
          onSelectionChange={selectWorkflow}
          onBack={() => selectWorkflow(null)}
          list={
            isLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">Loading…</p>
            ) : (
              <WorkflowList
                workflows={workflows ?? []}
                selectedUuid={selectedUuid}
                onSelect={selectWorkflow}
              />
            )
          }
          detail={
            selectedUuid ? (
              <WorkflowDetail workflowUuid={selectedUuid} />
            ) : (
              <div className="flex justify-center pt-24">
                <p className="text-[13px] text-[var(--ink-3)]">
                  Select a workflow to view its states, actions, and settings.
                </p>
              </div>
            )
          }
        />

        <WorkflowCreateDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onCreated={(uuid) => {
            selectWorkflow(uuid)
            setShowCreateDialog(false)
            toast.success('Workflow created')
          }}
          onError={(err) =>
            toastApiError(err, 'Failed to create workflow')
          }
        />
      </main>
    </AppShell>
  )
}
